import { useEffect, useState } from 'react';
import { CheckCircle2, XCircle } from 'lucide-react';
import RatingsWidget from '../RatingsWidget';

/**
 * Reusable "session ended" modal. Displayed to students when the teacher/host
 * ends the class. Auto-dismisses after `autoRedirectSeconds` and invokes
 * `onClose` so the parent page can navigate away (usually back to the
 * classes/rooms list).
 *
 * Props:
 *   open — whether the modal is visible
 *   title — heading text, default "Class has ended"
 *   message — body text
 *   endedBy — optional "by <teacher/host>" label
 *   onClose — called when user clicks close or the countdown completes
 *   autoRedirectSeconds — seconds before auto-close, default 5
 *   variant — 'class' | 'audio' (changes icon tint)
 *   rateContentType — when provided, embeds an inline rating widget
 *   rateContentId   — id for the rating widget
 *   rateTeacherId   — optional, also lets user rate the teacher
 */
export default function SessionEndedModal({
  open,
  title,
  message,
  endedBy,
  onClose,
  autoRedirectSeconds = 8,
  variant = 'class',
  rateContentType,
  rateContentId,
  rateTeacherId,
}) {
  const [remaining, setRemaining] = useState(autoRedirectSeconds);
  const [rateMode, setRateMode] = useState('content'); // 'content' | 'teacher' | 'done'

  useEffect(() => {
    if (!open) { setRemaining(autoRedirectSeconds); setRateMode('content'); return; }
    setRemaining(autoRedirectSeconds);
    setRateMode('content');
    const tick = setInterval(() => {
      setRemaining((r) => {
        if (r <= 1) { clearInterval(tick); onClose?.(); return 0; }
        return r - 1;
      });
    }, 1000);
    return () => clearInterval(tick);
  }, [open, autoRedirectSeconds, onClose]);

  if (!open) return null;

  const iconColor = variant === 'audio' ? 'text-purple-300' : 'text-amber-300';
  const iconBg = variant === 'audio' ? 'bg-purple-500/15' : 'bg-amber-500/15';
  const heading = title || (variant === 'audio' ? 'Audio room has ended' : 'Class has ended');
  const body = message || (variant === 'audio'
    ? 'The host has ended the audio room.'
    : 'The teacher has ended the class.');

  const showRating = !!(rateContentType && rateContentId);

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      data-testid="session-ended-modal"
      role="alertdialog"
      aria-modal="true"
    >
      <div className="w-full max-w-md rounded-2xl bg-slate-900 border border-slate-700 shadow-2xl overflow-hidden">
        <div className="p-6 flex flex-col items-center text-center">
          <div className={`w-14 h-14 rounded-full ${iconBg} flex items-center justify-center mb-4`}>
            <CheckCircle2 className={`w-7 h-7 ${iconColor}`} />
          </div>
          <h3 className="text-lg font-semibold text-white mb-1.5">{heading}</h3>
          <p className="text-sm text-slate-300 leading-relaxed">{body}</p>
          {endedBy && (
            <p className="text-xs text-slate-500 mt-2">Ended by {endedBy}</p>
          )}
          {showRating && rateMode === 'content' && (
            <div className="w-full mt-4 text-left">
              <RatingsWidget
                contentType={rateContentType}
                contentId={rateContentId}
                compact
                title="Quick rating"
                onSubmitted={() => setRateMode(rateTeacherId ? 'teacher' : 'done')}
              />
            </div>
          )}
          {showRating && rateMode === 'teacher' && rateTeacherId && (
            <div className="w-full mt-4 text-left">
              <RatingsWidget
                contentType="teacher"
                contentId={rateTeacherId}
                compact
                title="Rate your teacher too?"
                onSubmitted={() => setRateMode('done')}
              />
            </div>
          )}
          {showRating && rateMode === 'done' && (
            <p className="text-xs text-emerald-400 mt-3">Thanks for rating! ⭐</p>
          )}
          <p className="text-xs text-slate-500 mt-4">Returning to your dashboard in {remaining}s…</p>
        </div>
        <button
          onClick={onClose}
          className="w-full py-3 bg-slate-800 hover:bg-slate-700 text-slate-200 text-sm font-semibold border-t border-slate-700 flex items-center justify-center gap-2"
          data-testid="session-ended-close"
        >
          <XCircle className="w-4 h-4" /> Close now
        </button>
      </div>
    </div>
  );
}
